import React, { useEffect, useState, useRef } from 'react';
import { tourSteps } from '../../constants/tourSteps';
import { getEffectiveStepProps, getTargetRect, HighlightRect } from './tourUtils';

interface TourOverlayProps {
  currentStep: number;
  run: boolean;
  allowClicks: boolean;
}

export const TourOverlay: React.FC<TourOverlayProps> = ({ currentStep, run, allowClicks }) => {
  const [rect, setRect] = useState<HighlightRect | null>(null);
  const [opacity, setOpacity] = useState(0);
  const prevStepRef = useRef(-1);
  const rafRef = useRef<number | null>(null);
  const cleanupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Clear any pending timeouts when effect runs
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }

    if (!run) {
      setOpacity(0);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      cleanupTimeoutRef.current = setTimeout(() => { setRect(null); prevStepRef.current = -1; }, 300);
      return;
    }

    const step = tourSteps[currentStep];
    if (!step) { setRect(null); setOpacity(0); return; }

    const padding = (step as any).spotlightPadding ?? 8;
    const effectiveProps = getEffectiveStepProps(step);
    const isFirstStep = prevStepRef.current === -1;
    prevStepRef.current = currentStep;

    let lastRectStr = '';

    // Continuously track element position using RAF
    const trackPosition = () => {
      const newRect = getTargetRect(effectiveProps.target, padding);
      const newRectStr = JSON.stringify(newRect);
      if (newRect && newRectStr !== lastRectStr) {
        lastRectStr = newRectStr;
        setRect(newRect);
      } else if (!newRect && lastRectStr !== '') {
        // Target element is missing, hide overlay
        lastRectStr = '';
        setRect(null);
      }
      rafRef.current = requestAnimationFrame(trackPosition);
    };

    // Initial setup with fade transition
    if (isFirstStep) {
      // Start tracking immediately for first step
      rafRef.current = requestAnimationFrame(trackPosition);
      requestAnimationFrame(() => setOpacity(1));
    } else {
      // Fade out, then start tracking for subsequent steps
      setOpacity(0);
      fadeTimeoutRef.current = setTimeout(() => {
        rafRef.current = requestAnimationFrame(trackPosition);
        requestAnimationFrame(() => setOpacity(1));
      }, 200);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (cleanupTimeoutRef.current) clearTimeout(cleanupTimeoutRef.current);
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    };
  }, [currentStep, run]);

  if (!rect) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] transition-opacity duration-300"
      style={{ opacity, pointerEvents: allowClicks ? 'none' : 'auto' }}
    >
      {/* Dark overlay with hole */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <mask id="tour-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={rect.x}
              y={rect.y}
              width={rect.width}
              height={rect.height}
              rx="8"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(17, 24, 39, 0.6)"
          mask="url(#tour-spotlight-mask)"
        />
      </svg>

      {/* Spotlight border */}
      <div
        className="absolute rounded-lg border-2 border-white/30 transition-all duration-300 pointer-events-none"
        style={{
          left: rect.x,
          top: rect.y,
          width: rect.width,
          height: rect.height,
          boxShadow: '0 0 0 4px rgba(255, 255, 255, 0.1)',
        }}
      />
    </div>
  );
};
